import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommReportDefinitions, PresentationDefinition, Report, ReportDefinitions, ReportParameter } from 'src/app/models/report-data';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent {

	public reports: Array<Report>;
	public commReports: Array<Report>;
	public selectedReport: Report;
	public form: FormGroup

	public activeTab: number = 1;
	
	constructor(private fb: FormBuilder, private dataService: DataService, private siteService: SiteService) {}

	ngOnInit(): void {
		this.siteService.pageTitle = 'Reports';
		this.siteService.headerButtons = [
			{ text: 'Launch AI Assistant', class: 'neutral', callback: () => this.launchAiAssistant() },
			{ text: 'Run Report', class: 'neutral', callback: () => this.runReport() }
		];
		this.reports = ReportDefinitions;
		this.commReports = CommReportDefinitions;
	}

	launchAiAssistant() {
		
		
		this.dataService.getSsoLink().subscribe(
			(url: string) => {
				console.log("Received SSO URL:", url);
				if (url) {
					window.open(url, '_blank');
					this.siteService.setMessage("AI Assistant launched successfully");
				} else {
					this.siteService.setMessage("Unable to retrieve AI Assistant link", "error");
				}
			},
			error => {
				console.error("Error retrieving SSO link:", error);
				this.siteService.setMessage("Error launching AI Assistant", "error");
			}
		);
	}

	selectReport(r:Report) {
		let group:any = {};
		if(r?.parameters.length > 0) {

			r.parameters.forEach(p => {
				group[p.key] = new FormControl('');
				if(p.required) {
					group[p.key].Validators = [Validators.required];
				}
				if(p.type == 'select') {
					if(p.options) {
						// Options already populated (static) — skip API call
					} else {
						let apiCall;
						switch(p.api.method) {
							case 'getEmployers': apiCall = this.dataService.getEmployers().subscribe((resp: Array<any>) => {
									p.options = resp.map(r => ({ label: r[p.api.text], value: r[p.api.value] }));
									p.options.sort((a,b) => a.label < b.label ? -1 : (a.label > b.label ? 1 : 0))
								});
								break;
							case 'getCampaigns': apiCall = this.dataService.getAllCampaigns().subscribe((resp: Array<any>) => {
									p.options = resp.map(r => ({ label: r[p.api.text], value: r[p.api.value] }));
									p.options.sort((a,b) => a.label < b.label ? -1 : (a.label > b.label ? 1 : 0))
								});
								break;
							default: apiCall = this.dataService.getEmployers().subscribe((resp: Array<any>) => {
								p.options = resp.map(r => ({ label: r[p.api.text], value: r[p.api.value] }));
								p.options.sort((a,b) => a.label < b.label ? -1 : (a.label > b.label ? 1 : 0))
							});
							break;
						}
					}
				}
			})
			this.form = this.fb.group(group);
		} else {
			this.form = this.fb.group({});
		}
	}

	setTab(tab: number) {
		this.activeTab = tab;
		this.selectedReport = null;
		if(tab == 3) {
			this.selectedReport = { ...PresentationDefinition, parameters: PresentationDefinition.parameters.map(p => ({ ...p, options: p.options ? [...p.options] : undefined })) };
			this.selectReport(this.selectedReport);
		}
	}
	setDate(date: any, key: string) {
		this.form.get(key).setValue(date);
	}
	runReport() {
		if(!this.selectReport) return;
		this.form.markAllAsTouched();
		if(this.form.valid) {
			let params: Array<{name: string, value: any}> = [];
			Object.keys(this.form.controls).forEach(key => {
				if(!this.form.controls[key].value) {
					params.push({name: key, value: null })
				} else {
					switch(key) {
						case 'employerID':
							params.push({name: key, value: this.form.controls[key].value.value});
							break;
						case 'campaignID':
							params.push({name: key, value: this.form.controls[key].value.value});
							break;
						default:
							params.push({name: key, value: this.form.controls[key].value});
							break;
					}
				}
			})
			if(this.activeTab == 3) {
				let presData = {
					employerID: this.form.controls['employerID'].value?.value,
					quarter: this.form.controls['quarter'].value?.value,
					year: this.form.controls['year'].value?.value
				};
				this.dataService.runPresentation(presData).subscribe((data: any) => {
					if(data.byteLength == 0) {
						this.siteService.setMessage("There was no data returned for your report", "error");
						return;
					}
					const contentType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
					const blob = new Blob([data], { type: contentType });
					var a = document.createElement('a');
					document.body.appendChild(a);
					a.style.display = "none";
					const url = window.URL.createObjectURL(blob);
					a.href = url;
					a.download = "OneMorningPresentation_Engagement.pptx";
					a.click();
					URL.revokeObjectURL(a.href);
					a.remove();
					this.siteService.setMessage("Your report is being downloaded.");
				}, () => {
					this.siteService.setMessage("There was no data returned for your report", "error");
				})
			} else {
				let rptData = params.reduce((acc:any, p:any) => ({...acc, [p.name]: p.value}), { reportType: this.selectedReport.reportType });
				const apiMethod = this.activeTab == 1 ? this.dataService.runReport(rptData) : this.dataService.runCommReport(rptData);
				apiMethod.subscribe((data: any) => {
					if(data.byteLength == 0) {
						this.siteService.setMessage("There was no data returned for your report", "error");
						return;
					}
					const contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
					const blob = new Blob([data], { type: contentType });
					var a = document.createElement('a');
					document.body.appendChild(a);
					a.style.display = "none";
					const url = window.URL.createObjectURL(blob);
					a.href = url;
					a.download = this.selectedReport.name + ".xlsx";
					a.click();
					URL.revokeObjectURL(a.href);
					a.remove();
					this.siteService.setMessage("Your report is being downloaded.");
				}, () => {
					this.siteService.setMessage("There was no data returned for your report", "error");
				})
			}
		}
	}

}
